import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Handshake, ArrowRight, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import marketerApi, { MARKETER_TOKEN_KEY } from '../../lib/marketerApi';
import BrandLogo from '../../components/brand/BrandLogo';

// Handles BOTH flows:
//   • /affiliate/join/:token  → Professional (private invite, 10%)
//   • /affiliate/register     → Community (public, 5%)
export default function MarketerRegister() {
  const { token } = useParams();
  const isPro = !!token;
  const nav = useNavigate();
  const [checking, setChecking] = useState(isPro);
  const [inviteValid, setInviteValid] = useState(!isPro);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!isPro) return;
    marketerApi.get(`/affiliate/public/invitation/${token}`)
      .then(({ data }) => { setInviteValid(true); if (data.email) setForm((f) => ({ ...f, email: data.email })); })
      .catch(() => setInviteValid(false))
      .finally(() => setChecking(false));
  }, [isPro, token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error('Password must be at least 8 characters');
    setLoading(true);
    try {
      const url = isPro ? '/affiliate/public/register-professional' : '/affiliate/public/register-community';
      const payload = isPro ? { ...form, token } : form;
      const { data } = await marketerApi.post(url, payload);
      if (isPro) {
        // Professional marketers require Admin approval before first login.
        setPending(true);
      } else {
        localStorage.setItem(MARKETER_TOKEN_KEY, data.token);
        toast.success('Account created!');
        nav('/affiliate/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  if (checking) {
    return <div className="min-h-screen mesh-bg flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-fab-primary" /></div>;
  }

  if (isPro && pending) {
    return (
      <div className="min-h-screen mesh-bg flex items-center justify-center p-4">
        <div className="fab-card p-8 max-w-md text-center" data-testid="pro-pending">
          <ShieldCheck className="w-10 h-10 text-fab-primary mx-auto mb-3" />
          <h1 className="font-display text-xl font-bold text-fab-text mb-2">Registration submitted</h1>
          <p className="text-[13px] text-fab-text-secondary">Your Professional marketer account is <b>pending approval</b> by the FABTEXS team. You'll be able to sign in and get your referral link once it's activated.</p>
          <Link to="/affiliate" className="text-fab-primary font-semibold text-[13px] mt-4 inline-block">Go to Marketer Login</Link>
        </div>
      </div>
    );
  }

  if (isPro && !inviteValid) {
    return (
      <div className="min-h-screen mesh-bg flex items-center justify-center p-4">
        <div className="fab-card p-8 max-w-md text-center" data-testid="invite-invalid">
          <h1 className="font-display text-xl font-bold text-fab-text mb-2">Invitation unavailable</h1>
          <p className="text-[13px] text-fab-text-secondary">This professional invitation link is invalid, already used, or has been revoked. Please contact the FABTEXS team.</p>
          <Link to="/affiliate" className="text-fab-primary font-semibold text-[13px] mt-4 inline-block">Go to Marketer Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh-bg flex items-center justify-center p-4" data-testid="marketer-register">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center justify-center mb-5"><BrandLogo height={36} /></Link>
        <div className="fab-card p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-1">
            {isPro ? <ShieldCheck className="w-5 h-5 text-fab-primary" /> : <Handshake className="w-5 h-5 text-fab-primary" />}
            <h1 className="font-display text-xl font-bold text-fab-text">{isPro ? 'Professional Marketer' : 'Become a Marketer'}</h1>
          </div>
          <p className="text-[13px] text-fab-text-secondary mb-5">
            {isPro ? 'You have been invited. Earn 10% on every referred subscription.' : 'Join free and earn 5% on every referred subscription.'}
          </p>
          <form onSubmit={submit} className="space-y-3">
            <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Full name" data-testid="register-name" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
            <input value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="Email" type="email" data-testid="register-email" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
            <input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Mobile number" data-testid="register-phone" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
            <input value={form.password} onChange={(e) => set('password', e.target.value)} placeholder="Password (min 8 chars)" type="password" data-testid="register-password" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
            <button type="submit" disabled={loading} data-testid="register-submit" className="btn-primary w-full py-2.5 text-[14px] flex items-center justify-center gap-2">
              {loading ? 'Creating…' : <>Create Account <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
          <div className="text-[13px] text-fab-text-secondary mt-4 text-center">
            Already a marketer? <Link to="/affiliate" className="text-fab-primary font-semibold">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
